import { useCallback, useRef, useState, type KeyboardEvent } from 'react'
import { ArrowUp } from 'lucide-react'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { AttachButton } from './AttachButton'
import { MentionPopup } from './MentionPopup'
import { MAX_ATTACHMENTS, type AttachedFile } from '@/lib/api'
import { activeMention, applyMention, type MentionedCustomer } from '@/lib/mentions'
import type { CustomerMatch } from '@/hooks/useCustomerSearch'
import { useBranding } from '@/contexts/BrandingContext'

interface MessageInputProps {
  value: string
  onChange: (text: string) => void
  onSend: () => void
  isLoading: boolean
  placeholder?: string
  pendingFiles: AttachedFile[]
  fileError: string | null
  onRemoveFile: (index: number) => void
  onAttachFile: () => void
  onTakePhoto: () => void
  /**
   * Enables "@" customer mentions. OPERATOR SURFACES ONLY — a customer must
   * never be handed a search over the customer base.
   */
  enableMentions?: boolean
  /** Records the chosen customer so their id can ride along at send time. */
  onMention?: (customer: MentionedCustomer) => void
}

export function MessageInput({
  value,
  onChange,
  onSend,
  isLoading,
  placeholder,
  pendingFiles,
  fileError,
  onRemoveFile,
  onAttachFile,
  onTakePhoto,
  enableMentions,
  onMention,
}: MessageInputProps) {
  const branding = useBranding()
  const canSend = value.trim().length > 0 && !isLoading
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  // Which @-token is open depends on the caret, and a textarea does not report it
  // through onChange — so it is measured on every event that can move it, and
  // stored WITH the text it was measured against. `value` is owned by the parent
  // and gets replaced wholesale (transcript handoff, /progress prefill, autoAsk);
  // a caret index left over from the previous text would point into the middle of
  // the new one. When the pair is stale, end-of-text is the right assumption.
  const [selection, setSelection] = useState<{ value: string; caret: number } | null>(null)
  const [dismissedAt, setDismissedAt] = useState<number | null>(null)
  const mentionKeyHandler = useRef<((key: string) => boolean) | null>(null)

  const caret = selection?.value === value ? selection.caret : value.length
  const mention = enableMentions ? activeMention(value, caret) : null
  // Escape closes the list for THAT token only — moving on and typing a new '@'
  // must reopen it, or the feature dies for the rest of the message.
  const mentionOpen = Boolean(mention) && mention!.start !== dismissedAt

  const syncCaret = (el: HTMLTextAreaElement) =>
    setSelection({ value: el.value, caret: el.selectionStart ?? el.value.length })

  const choose = (m: CustomerMatch) => {
    if (!mention) return
    const customer: MentionedCustomer = { id: m.id, name: m.name ?? 'Unnamed customer' }
    const next = applyMention(value, mention, caret, customer)
    onChange(next.text)
    onMention?.(customer)
    setDismissedAt(null)
    setSelection({ value: next.text, caret: next.caret })
    // The value lands via props on the next render, so the caret has to be
    // restored after it — otherwise it jumps to the end of the message.
    requestAnimationFrame(() => {
      const el = textareaRef.current
      if (!el) return
      el.focus()
      el.setSelectionRange(next.caret, next.caret)
    })
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionOpen && mentionKeyHandler.current?.(e.key)) {
      e.preventDefault()
      return
    }
    // Enter sends and Shift+Enter inserts a newline.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (canSend) onSend()
    }
  }

  const registerMentionKeys = useCallback((handler: ((key: string) => boolean) | null) => {
    mentionKeyHandler.current = handler
  }, [])

  return (
    <>
      {/* Pending file badges — one row per attachment, each removable */}
      {pendingFiles.length > 0 && (
        <div className="flex flex-col border-t border-blue-200 bg-blue-50 px-4 py-2">
          {pendingFiles.map((file, i) => (
            <div key={`${file.name}-${i}`} className="flex items-center justify-between gap-2 py-px">
              <span className="min-w-0 flex-1 truncate text-[13px] text-blue-700">
                {String.fromCodePoint(0x1f4ce)} {file.name} ({(file.size / 1024).toFixed(0)} KB)
              </span>
              <button
                type="button"
                onClick={() => onRemoveFile(i)}
                aria-label={`Remove ${file.name}`}
                className="shrink-0 cursor-pointer px-1 text-[14px] text-muted-foreground"
              >
                ✕
              </button>
            </div>
          ))}
          {pendingFiles.length >= MAX_ATTACHMENTS && (
            <span className="mt-px text-[11.5px] text-blue-700/70">
              {MAX_ATTACHMENTS} of {MAX_ATTACHMENTS} attached — remove one to add another.
            </span>
          )}
        </div>
      )}

      {/* File error banner */}
      {fileError && (
        <div className="border-t border-rose-200 bg-rose-50 px-4 py-2">
          <p className="text-[12px] text-rose-700">{fileError}</p>
        </div>
      )}

      {mentionOpen && mention ? (
        <MentionPopup
          query={mention.query}
          onChoose={choose}
          onDismiss={() => setDismissedAt(mention.start)}
          onKeyHandlerReady={registerMentionKeys}
        />
      ) : null}

      {/* Input row */}
      <div className="flex items-end gap-2 border-t border-border bg-background p-3">
        <AttachButton
          onAttachFile={onAttachFile}
          onTakePhoto={onTakePhoto}
          disabled={isLoading || pendingFiles.length >= MAX_ATTACHMENTS}
        />
        <Textarea
          ref={textareaRef}
          className="max-h-[120px] min-h-10 min-w-0 flex-1 resize-none rounded-[20px] border-0 bg-muted px-4 py-2.5 text-[15px] text-foreground shadow-none placeholder:text-muted-foreground md:text-[15px]"
          value={value}
          onChange={e => {
            onChange(e.target.value)
            syncCaret(e.target)
          }}
          onKeyUp={e => syncCaret(e.currentTarget)}
          onClick={e => syncCaret(e.currentTarget)}
          onSelect={e => syncCaret(e.currentTarget)}
          placeholder={placeholder ?? 'Message…'}
          rows={1}
          maxLength={2000}
          enterKeyHint="send"
          onKeyDown={handleKeyDown}
          disabled={isLoading}
        />
        <button
          type="button"
          className={cn(
            'flex h-[60px] w-[60px] shrink-0 cursor-pointer items-center justify-center rounded-[18px]',
            !canSend && 'bg-slate-300',
          )}
          style={canSend ? { backgroundColor: branding.primaryColor } : undefined}
          onClick={onSend}
          disabled={!canSend}
          aria-label="Send"
        >
          <ArrowUp size={18} className="text-white" />
        </button>
      </div>
    </>
  )
}
